import { useCallback } from 'react';

import { InventoryTabItemName, inventoryStore } from '../InventoryStore';
import { isEnabledByMessage } from '../_components/TabViews/tabViewUtils';
import { getChangedFormFields } from '../utils';
import {
  updateExperienceStockForDate,
  updateMaxBookingForDate,
  updateRoomFieldsForDate,
} from '../utils/inventoryUpdates';

/**
 * Creates a memoized callback for duplicating a field across dates.
 * Reads mutable state (modifiedInventory, duplicationDateSelection) directly
 * from the store at call time so the callback reference stays stable.
 * @param entityType - Type of entity (rooms, experiences, packs)
 * @param entityId - ID of the entity (roomId, experienceId, etc.)
 * @param fieldName - Name of the field to duplicate (e.g., 'remainingStock', 'discount')
 * @returns Stable memoized callback function
 */
export const useDuplicateCallback = (
  entityType: InventoryTabItemName,
  entityId: number,
  fieldName: string
) => {
  return useCallback(
    (value?: number | null) => {
      const {
        form,
        modifiedInventory,
        dependencies,
        setModifiedInventory,
        duplicationDateSelection,
      } = inventoryStore.getState();

      if (!dependencies || (value !== null && isNaN(value ?? NaN))) {
        return;
      }

      let currentInventory = modifiedInventory ?? {};

      duplicationDateSelection.forEach((date) => {
        const existingData =
          entityType === InventoryTabItemName.Rooms
            ? modifiedInventory?.[date]?.rooms?.[entityId]
            : entityType === InventoryTabItemName.Experiences
              ? modifiedInventory?.[date]?.experiences?.[entityId]
              : modifiedInventory?.[date]?.packs?.[entityId];

        if (!existingData) {
          return;
        }

        if (!isEnabledByMessage(existingData.message)) {
          return;
        }

        const updateConfig = {
          [InventoryTabItemName.Rooms]: updateRoomFieldsForDate(
            currentInventory,
            entityId,
            date,
            { [fieldName]: value },
            dependencies
          ),
          [InventoryTabItemName.Experiences]: updateExperienceStockForDate(
            currentInventory,
            entityId,
            date,
            value ?? null,
            dependencies
          ),
          [InventoryTabItemName.Packs]: updateMaxBookingForDate(
            currentInventory,
            entityId,
            date,
            value ?? null,
            dependencies
          ),
        };

        currentInventory = updateConfig[entityType];

        const newEntry = (
          entityType === InventoryTabItemName.Rooms
            ? currentInventory[date]?.rooms?.[entityId]
            : entityType === InventoryTabItemName.Experiences
              ? currentInventory[date]?.experiences?.[entityId]
              : currentInventory[date]?.packs?.[entityId]
        ) as Record<string, unknown> | undefined;

        const prevEntry = (
          entityType === InventoryTabItemName.Rooms
            ? modifiedInventory?.[date]?.rooms?.[entityId]
            : entityType === InventoryTabItemName.Experiences
              ? modifiedInventory?.[date]?.experiences?.[entityId]
              : modifiedInventory?.[date]?.packs?.[entityId]
        ) as Record<string, unknown> | undefined;

        const formFields = getChangedFormFields(
          prevEntry,
          newEntry,
          date,
          entityId
        );

        setModifiedInventory(currentInventory);

        if (formFields.length > 0) {
          form?.setFields(formFields);
        }
      });
    },
    [entityType, entityId, fieldName]
  );
};
